import { createStore, createEvent, createEffect, combine } from 'effector';
import { get } from 'lodash';
import update from 'immutability-helper';
import IPost from '@interfaces/IPost';
import request from '@helpers/request';
import api from '@constants/endpoints';
import { PaginationProps } from 'antd/lib/pagination';
import createPaginationStore from './pagination';
import createPreloadersStore from './preloaders';
import createFiltersStore from './filters';
import removeUndefinedFilters from '@helpers/removeUndefinedFilters';
import VIEW_STATUSES from '@constants/viewStatuses';

export const reset = createEvent('Posts store reset');

const defaultFilters = {
  sort: 'createdDateTime,desc',
  visibleBlockPost: 'UNBLOCK',
};

let path = ['data', 'content'];

export const getList = createEffect({
  name: 'getList',
  async handler(params?: { pagination?: PaginationProps; filters?: Record<string, any> }) {
    const paginationParams: PaginationProps = {
      ...pagination.getState(),
      ...params?.pagination,
    };
    let filtersParams = filters.getState();
    if (params?.filters) {
      setFilters(params.filters);
      filtersParams = params.filters;
      if (!params.pagination) {
        paginationParams.current = 1;
      }
    }

    const { current } = paginationParams;

    let response;

    if (filtersParams.visibleBlockPost === 'TOP') {
      response = await request(api.posts.getTopList, {
        urlParams: { gameId: filtersParams.gameId },
        params: {
          paged: true,
          size: localStorage.getItem('postsPageSize'),
          page: (current || 1) - 1,
        },
      });
      path = ['data', 'body'];
    } else {
      response = await request(api.posts.getList, {
        params: {
          paged: true,
          size: localStorage.getItem('postsPageSize'),
          page: (current || 1) - 1,
          ...defaultFilters,
          ...removeUndefinedFilters(filtersParams),
        },
      });
      path = ['data', 'content'];
    }

    return response;
  },
});

export const block = createEffect({
  name: 'block',
  async handler({ postId }: { postId: IPost['id']; preloaderId: string }) {
    const response = await request(api.posts.setStatus, {
      urlParams: { postId },
      params: {
        viewStatus: VIEW_STATUSES.BAN,
      },
    });
    return response;
  },
});

export const unblock = createEffect({
  name: 'unblock',
  async handler({ postId }: { postId: IPost['id']; preloaderId: string }) {
    const response = await request(api.posts.setStatus, {
      urlParams: { postId },
      params: {
        viewStatus: VIEW_STATUSES.ALL,
      },
    });
    return response;
  },
});

export const hidePost = createEffect({
  name: 'hidePost',
  async handler({ postId }: { postId: IPost['id']; preloaderId: string }) {
    const response = await request(api.posts.setStatus, {
      urlParams: { postId },
      params: {
        viewStatus: VIEW_STATUSES.SHADOW,
      },
    });
    return response;
  },
});

export const unhidePost = createEffect({
  name: 'unhidePost',
  async handler({ postId }: { postId: IPost['id']; preloaderId: string }) {
    const response = await request(api.posts.setStatus, {
      urlParams: { postId },
      params: {
        viewStatus: VIEW_STATUSES.ALL,
      },
    });
    return response;
  },
});

export const facePost = createEffect({
  name: 'facePost',
  async handler({ postId }: { postId: IPost['id']; preloaderId: string }) {
    const response = await request(api.posts.postMarks, {
      data: { postId, markType: VIEW_STATUSES.FACE },
    });
    return response;
  },
});

export const removefacePost = createEffect({
  name: 'removefacePost',
  async handler({ postId }: { postId: IPost['id']; preloaderId: string }) {
    const responseId = await request(api.posts.getMarks, {
      params: {
        post_id: postId,
      },
    });
    const response = await request(api.posts.delMarks, {
      params: {
        id: responseId?.data?.body[0]?.id,
      },
    });
    return response;
  },
});

const { pagination, setPagination } = createPaginationStore({
  getList,
  listName: 'posts',
  resetEvent: reset,
  bodyPath: ['data'],
  syncWithQuery: true,
});

export { setPagination };

const preloaders = createPreloadersStore([
  getList,
  block,
  unblock,
  hidePost,
  unhidePost,
  facePost,
  removefacePost,
]);

const { filters, setFilters } = createFiltersStore({
  listName: 'posts',
  deaultValues: defaultFilters,
  resetEvent: reset,
  syncWithQuery: true,
});

export { setFilters };

export const updateItemHandler = (
  state: IPost[],
  {
    result: { data },
    params: { postId },
  }: { result: { data: IPost }; params: { postId: IPost['id'] } },
) => {
  const index = state.findIndex((p) => p.id === postId);
  if (index >= 0) {
    const updatedState: any = update(state, {
      $splice: [[index, 1, data]],
    });
    return updatedState;
  } else {
    return state;
  }
};

export const updateFaceItemHandler = (
  state: IPost[],
  { params: { postId } }: { result: { data: IPost }; params: { postId: IPost['id'] } },
) => {
  const index = state.findIndex((p) => p.id === postId);
  if (index >= 0) {
    const updatedState: any = update(state, {
      $splice: [[index, 1, { ...state[index], marks: [{ markType: VIEW_STATUSES.FACE }] }]],
    });
    return updatedState;
  } else {
    return [
      ...state,
      { id: postId, marks: [{ markType: VIEW_STATUSES.FACE }] } as unknown as IPost,
    ];
  }
};

export const updateRemovefaceItemHandler = (
  state: IPost[],
  { params: { postId } }: { result: { data: IPost }; params: { postId: IPost['id'] } },
) => {
  const index = state.findIndex((p) => p.id === postId);
  if (index >= 0) {
    const updatedState: any = update(state, {
      $splice: [[index, 1, { ...state[index], marks: [{ markType: null }] }]],
    });
    return updatedState;
  } else {
    return [...state, { id: postId, marks: [{ markType: null }] } as unknown as IPost];
  }
};

export const list = createStore<IPost[]>([])
  .on(getList.done, (state, { result }) => {
    return get(result, path);
  })
  .on(getList.fail, () => [])
  .on(block.done, updateItemHandler)
  .on(unblock.done, updateItemHandler)
  .on(hidePost.done, updateItemHandler)
  .on(unhidePost.done, updateItemHandler)
  .on(facePost.done, updateFaceItemHandler)
  .on(removefacePost.done, updateRemovefaceItemHandler)
  .reset(reset);

const posts = combine({ preloaders, list, pagination, filters });

export default posts;
